<script setup lang="ts">
import { DefinitionDto } from '@/api/generator';
import Dialog from '@/components/Dialog/Dialog.vue';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import { FormMode } from '@/components/forms/types.ts';
import { useFormMode } from '@/components/forms/useFormMode.ts';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import DefinitionParameterSetModal from '@/views/quantities/components/useInEvaluations/DefinitionParameterSetModal.vue';
import DefinitionParameterSetsTable from '@/views/quantities/components/useInEvaluations/DefinitionParameterSetsTable.vue';
import { mdiPlus } from '@mdi/js';
import { useToggle } from '@vueuse/core';
import { cloneDeep } from 'lodash';
import { useField } from 'vee-validate';
import { computed, ref, toRef, useTemplateRef, watchEffect } from 'vue';

type DefinitionParameterSetsProps = {
  name: string;
  disabled?: boolean;
  allowExtend: boolean;
};
const props = defineProps<DefinitionParameterSetsProps>();

type DefinitionParameterSetsEmits = {
  update: [DefinitionDto];
};
const emit = defineEmits<DefinitionParameterSetsEmits>();

const { value: definition } = useField<DefinitionDto>(toRef(() => props.name));

const internalDefinition = ref<DefinitionDto>(cloneDeep(definition.value));

watchEffect(() => {
  internalDefinition.value = cloneDeep(definition.value);
});

const mode = useFormMode();

const hasDefinitionParameterValueSets = computed(
  () =>
    internalDefinition.value.definitionParameterValueSets?.length !== undefined &&
    internalDefinition.value.definitionParameterValueSets?.length > 0 &&
    internalDefinition.value.definitionParameterValueSets?.every(
      (set) => set?.values?.length === internalDefinition.value?.definitionParameters?.length,
    ),
);
const hasDefinitionParameters = computed(
  () =>
    typeof internalDefinition.value.definitionParameters?.length === 'number' &&
    internalDefinition.value.definitionParameters?.length > 0,
);

const definitionParameterSetIdentifier = ref<string | undefined>(undefined);
const definitionParameterSetFormMode = ref<FormMode>('add');
const [isDialogOpen, toggleDialog] = useToggle(false);
function toggleEditParameterValueSetDialog(setIdentifier: string) {
  definitionParameterSetFormMode.value = 'edit';
  definitionParameterSetIdentifier.value = setIdentifier;
  toggleDialog(true);
}
function updateDefinitionParameterValueSet(forDefinition: DefinitionDto) {
  internalDefinition.value = forDefinition;
  toggleDialog(false);
  emit('update', forDefinition);
}
function deleteDefinitionParameterValueSet(setIdentifier: string) {
  const definitionParameterSetIndex =
    internalDefinition.value.definitionParameterValueSets?.findIndex(
      (p) => p.setIdentifier === setIdentifier,
    );
  if (definitionParameterSetIndex === undefined) {
    return;
  }
  internalDefinition.value.definitionParameterValueSets?.splice(definitionParameterSetIndex, 1);
  emit('update', internalDefinition.value);
}

const definitionParameterSetModalRef = useTemplateRef<
  InstanceType<typeof DefinitionParameterSetModal>
>('definitionParameterSetModal');
</script>
<template>
  <div v-if="hasDefinitionParameters" class="definition-parameter-sets">
    <div class="d-flex mt-4">
      <h6>{{ $t('DefinitionParameterSet_other') }}</h6>
      <VSpacer />
      <Tooltip :disabled="hasDefinitionParameters">
        <template #target="{ props: tooltipProps }">
          <div v-bind="tooltipProps" style="cursor: pointer">
            <GhostVBtn
              v-if="mode !== 'details'"
              :prependIcon="mdiPlus"
              :text="$t('AddDefinitionParameterSet')"
              size="x-small"
              :disabled="!allowExtend && (disabled || !hasDefinitionParameters)"
              @click="
                definitionParameterSetFormMode = 'add';
                definitionParameterSetIdentifier = undefined;
                toggleDialog(true);
              "
            />
          </div>
        </template>
        <template #tooltip>
          {{ $t('AtLeastOneDefinitionParameterMustBeDefined') }}
        </template>
      </Tooltip>
    </div>

    <template v-if="hasDefinitionParameters">
      <DefinitionParameterSetsTable
        v-if="hasDefinitionParameterValueSets"
        :name="name"
        :allowExtend="allowExtend"
        @edit="toggleEditParameterValueSetDialog"
        @delete="deleteDefinitionParameterValueSet"
      />
    </template>
    <Dialog v-if="mode !== 'details' && isDialogOpen" v-model="isDialogOpen" size="large">
      <template #title>
        {{
          definitionParameterSetFormMode === 'add'
            ? $t('AddDefinitionParameterSet')
            : $t('EditDefinitionParameterSet')
        }}
      </template>

      <template #text>
        <DefinitionParameterSetModal
          ref="definitionParameterSetModal"
          v-model:definitionParameterSetIdentifier="definitionParameterSetIdentifier"
          v-model:definitionParameterSetFormMode="definitionParameterSetFormMode"
          :name="name"
          :allowExtend="allowExtend"
          @update="updateDefinitionParameterValueSet($event)"
          @close="toggleDialog(false)"
        />
      </template>
      <template #actions>
        <div class="d-flex justify-end mb-5">
          <TextVBtn
            class="mr-6"
            :text="$t('Cancel')"
            @click="definitionParameterSetModalRef?.onCancel"
          />

          <VeeSubmitButton
            :text="
              definitionParameterSetFormMode === 'edit'
                ? $t('Apply')
                : $t('AddDefinitionParameterSet')
            "
            @click="definitionParameterSetModalRef?.submit"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>
